'use client';

import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import type { ContractorProfile } from '@hummii/shared-types';

interface ProfileContractorModalProps extends BaseModalProps {
  contractor?: ContractorProfile | null;
  onContact?: (contractorId: string) => void;
  onViewPortfolio?: (contractorId: string) => void;
}

/**
 * Profile Contractor Modal - displays detailed contractor profile
 * Usage: const { open } = useModal('profile-contractor'); open({ contractor });
 */
export function ProfileContractorModal({
  isOpen,
  onClose,
  contractor,
  onContact,
  onViewPortfolio,
}: ProfileContractorModalProps) {
  if (!contractor) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        animation="slide-up"
        title="Contractor Profile"
      >
        <div className="text-center py-8">
          <p className="text-gray-500">Contractor information not available</p>
        </div>
      </Modal>
    );
  }

  const { user, bio, hourlyRate, experience, businessName, city, province, rating, reviewCount, completedOrders, categories, portfolio, isAvailable } = contractor;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      animation="slide-up"
      title="Contractor Profile"
      showCloseButton
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4 pb-6 border-b border-gray-200">
          {/* Avatar */}
          <div className="relative">
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            {isAvailable && (
              <div className="absolute bottom-0 right-0 w-5 h-5 bg-green-500 border-2 border-white rounded-full" title="Available" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-gray-900">
                {businessName || user.name}
              </h3>
              {/* Verified badge disabled (Identity Verification removed) */}
              {/* {user.isVerified && (
                <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )} */}
            </div>

            {/* Location */}
            {(city || province) && (
              <p className="text-sm text-gray-600 mb-2">
                {city}{city && province ? ', ' : ''}{province}
              </p>
            )}

            {/* Rating */}
            {rating !== null && (
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className={`w-4 h-4 ${
                        i < Math.round(Number(rating))
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                      />
                    </svg>
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  {Number(rating).toFixed(1)} ({reviewCount} reviews)
                </span>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-900">{completedOrders}</span>
                <span className="text-gray-600"> completed jobs</span>
              </div>
              {experience && (
                <div>
                  <span className="font-semibold text-gray-900">{experience}</span>
                  <span className="text-gray-600"> years experience</span>
                </div>
              )}
            </div>
          </div>

          {/* Hourly Rate */}
          {hourlyRate && (
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">
                ${hourlyRate}
              </p>
              <p className="text-xs text-gray-500">per hour</p>
            </div>
          )}
        </div>

        {/* Categories */}
        {categories && categories.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              Services
            </h4>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <span
                  key={cat.id}
                  className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full"
                >
                  {cat.category.nameEn}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Bio */}
        {bio && (
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2">About</h4>
            <p className="text-gray-700 whitespace-pre-wrap">{bio}</p>
          </div>
        )}

        {/* Portfolio Preview */}
        {portfolio && portfolio.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900">
                Portfolio ({portfolio.length})
              </h4>
              <button
                type="button"
                onClick={() => onViewPortfolio?.(contractor.id)}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                View All
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {portfolio.slice(0, 6).map((item) => (
                <div
                  key={item.id}
                  className="aspect-square rounded-lg overflow-hidden bg-gray-100"
                >
                  <img
                    src={item.imageUrl}
                    alt={item.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                    onClick={() => onViewPortfolio?.(contractor.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => onContact?.(contractor.id)}
            disabled={!isAvailable}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isAvailable ? 'Contact' : 'Unavailable'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
